// localStorage-based app watchlist (issue #18).
// Stores up to MAX_ITEMS apps; overflow prunes the least-recently-used entry.
// All functions are pure with respect to external state — side effects are
// limited to localStorage reads/writes so they can be unit tested easily.

const STORAGE_KEY = 'gar_watchlist';
const MAX_ITEMS = 20;

/**
 * @typedef {object} WatchedApp
 * @property {number|string} trackId
 * @property {string} trackName
 * @property {string} artworkUrl   - Best available artwork URL (100px or 60px).
 * @property {string} lastCompared - ISO-8601 timestamp of most recent comparison.
 */

/**
 * Read the current watchlist from localStorage.
 * @returns {WatchedApp[]} Sorted newest-first.
 */
export function getWatchlist() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.slice().sort((a, b) => b.lastCompared.localeCompare(a.lastCompared));
  } catch {
    return [];
  }
}

/**
 * Return true when trackId is already in the watchlist.
 * @param {number|string} trackId
 */
export function isWatching(trackId) {
  return getWatchlist().some((a) => String(a.trackId) === String(trackId));
}

/**
 * Add or update an app in the watchlist.
 * If the list is already at MAX_ITEMS, the oldest entry is replaced.
 *
 * @param {{ trackId: number|string, trackName: string, artworkUrl100?: string, artworkUrl60?: string }} app
 */
export function addToWatchlist(app) {
  const list = getWatchlist();
  const existingIndex = list.findIndex((a) => String(a.trackId) === String(app.trackId));

  const entry = {
    trackId: app.trackId,
    trackName: app.trackName,
    artworkUrl: app.artworkUrl100 ?? app.artworkUrl60 ?? '',
    lastCompared: new Date().toISOString(),
  };

  if (existingIndex !== -1) {
    list[existingIndex] = entry;
  } else {
    if (list.length >= MAX_ITEMS) {
      // Prune oldest (list is sorted newest-first, so last item is oldest).
      list.splice(list.length - 1, 1);
    }
    list.push(entry);
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Storage quota exceeded — silently skip.
  }
}

/**
 * Remove an app from the watchlist by trackId.
 * @param {number|string} trackId
 */
export function removeFromWatchlist(trackId) {
  const list = getWatchlist().filter((a) => String(a.trackId) !== String(trackId));
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // ignore
  }
}
